import { ActivityLogEvent, ModelType } from "@/gql/graphql";
import { ReactNode } from "react";
import { DefaultActivityLogItem } from "@/app/ActivityLogs/Components/ActivityLogEventItems/DefaultActivityLogItem";
import { NotationActivityLogItem } from "@/app/ActivityLogs/Components/ActivityLogEventItems/NotationActivityLogItem";
import { EmailActivityLogItem } from "@/app/ActivityLogs/Components/ActivityLogEventItems/EmailActivityLogItem";

import {
  ArchiveIcon,
  PauseCircleIcon,
  CheckCircle2Icon,
  CalendarCheckIcon,
  CopyIcon,
  UnlockIcon,
  UserCheckIcon,
  UserXIcon,
  FileQuestionIcon,
  UserSearchIcon,
  FileCheckIcon,
  FileTextIcon,
  FileXIcon,
  LinkIcon,
  XCircleIcon,
  CheckSquareIcon,
  BanIcon,
  CreditCardIcon,
  WalletIcon,
  AlertCircleIcon,
  TimerIcon,
  ShieldCheckIcon,
  UnlinkIcon,
  CrownIcon,
  PlusCircleIcon,
  Trash2Icon,
  PhoneIcon,
  RefreshCwIcon,
  HandCoinsIcon,
  AlertTriangleIcon,
  CheckCircleIcon as LucideCheckCircleIcon,
  SendIcon,
  MegaphoneIcon,
  MailCheckIcon,
  MessageSquareIcon,
  HourglassIcon,
  FileSpreadsheetIcon,
  ShieldBanIcon,
  TruckIcon,
  PackageCheckIcon,
  CircleCheckBigIcon,
  BanknoteIcon,
  StarIcon,
  CircleDotIcon,
  FileWarningIcon,
  RotateCcwIcon,
} from "lucide-react";
import { ActivityLogListItem } from "@/app/ActivityLogs/GraphQL/activityLogListQuery";

const activityLogEventNodes: {
  [key in ActivityLogEvent]: (
    log: ActivityLogListItem,
    modelType: ModelType,
    modelId: string,
  ) => ReactNode;
} = {
  RELOCATION_ARCHIVED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={ArchiveIcon}
      iconColor="text-gray-400"
    />
  ),
  RELOCATION_EXPIRED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={HourglassIcon}
      iconColor="text-orange-400"
    />
  ),
  RELOCATION_PAUSED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={PauseCircleIcon}
      iconColor="text-amber-400"
    />
  ),
  RELOCATION_READY: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CheckCircle2Icon}
      iconColor="text-emerald-400"
    />
  ),
  RELOCATION_BOOKED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CalendarCheckIcon}
      iconColor="text-blue-400"
    />
  ),
  RELOCATION_DUPLICATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CopyIcon}
      iconColor="text-yellow-400"
    />
  ),
  RELOCATION_RELEASED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UnlockIcon}
      iconColor="text-green-400"
    />
  ),
  RELOCATION_PRO_DRIVER_REQUESTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserSearchIcon}
      iconColor="text-indigo-400"
    />
  ),
  BOOKING_CLAIMED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserCheckIcon}
      iconColor="text-green-400"
    />
  ),
  WORK_ORDER_QUOTE_REQUESTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileQuestionIcon}
      iconColor="text-purple-400"
    />
  ),
  WORK_ORDER_ARCHIVED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={ArchiveIcon}
      iconColor="text-purple-400"
    />
  ),
  WORK_ORDER_DRIVERS_ASSIGNED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserSearchIcon}
      iconColor="text-yellow-400"
    />
  ),
  WORK_ORDER_IN_PROGRESS: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={TruckIcon}
      iconColor="text-sky-400"
    />
  ),
  WORK_ORDER_INVOICED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileSpreadsheetIcon}
      iconColor="text-blue-400"
    />
  ),
  WORK_ORDER_COMPLETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CircleCheckBigIcon}
      iconColor="text-green-400"
    />
  ),
  WORK_ORDER_ASSIGNING_DRIVERS: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileCheckIcon}
      iconColor="text-sky-400"
    />
  ),
  WORK_ORDER_QUOTE_ISSUED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileTextIcon}
      iconColor="text-blue-400"
    />
  ),
  WORK_ORDER_QUOTE_ACCEPTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileCheckIcon}
      iconColor="text-green-400"
    />
  ),
  WORK_ORDER_DRIVER_ASSIGNED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserCheckIcon}
      iconColor="text-green-400"
    />
  ),
  WORK_ORDER_DRIVER_UNASSIGNED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserXIcon}
      iconColor="text-red-400"
    />
  ),
  WORK_ORDER_QUOTE_REJECTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileXIcon}
      iconColor="text-red-400"
    />
  ),
  BOOKING_ASSIGNED_LINE: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={LinkIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_CANCELLED: (log) => (
    <DefaultActivityLogItem log={log} Icon={BanIcon} iconColor="text-red-400" />
  ),
  BOOKING_CHANGE_REQUEST_APPROVED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CheckSquareIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_CHANGE_REQUEST_PLACED: (log) => (
    <NotationActivityLogItem log={log} titleKey="requestedChange" />
  ),
  BOOKING_CHANGE_REQUEST_REJECTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={XCircleIcon}
      iconColor="text-red-400"
    />
  ),
  BOOKING_CONFIRMED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={ShieldCheckIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_PAYMENT_REQUEST_CANCELLED: (log) => (
    <DefaultActivityLogItem log={log} Icon={BanIcon} iconColor="text-red-400" />
  ),
  BOOKING_PAYMENT_REQUEST_CAPTURED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={WalletIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_PAYMENT_REQUEST_CREATED: (log) => (
    <NotationActivityLogItem log={log} titleKey="requestedPayment" />
  ),
  BOOKING_PAYMENT_REQUEST_PENDING: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CreditCardIcon}
      iconColor="text-yellow-400"
    />
  ),
  BOOKING_PENDING: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={TimerIcon}
      iconColor="text-yellow-400"
    />
  ),
  BOOKING_ACCEPTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CheckSquareIcon}
      iconColor="text-sky-400"
    />
  ),
  BOOKING_RELEASED_LINE: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UnlinkIcon}
      iconColor="text-red-400"
    />
  ),
  BOOKING_VIP: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CrownIcon}
      iconColor="text-purple-400"
    />
  ),
  CREATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={PlusCircleIcon}
      iconColor="text-green-400"
    />
  ),
  DELETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={Trash2Icon}
      iconColor="text-red-400"
    />
  ),
  EMAIL: (log, modelType, modelId) => (
    <EmailActivityLogItem log={log} modelType={modelType} modelId={modelId} />
  ),
  EMAIL_FAILED: (log, modelType, modelId) => (
    <EmailActivityLogItem
      log={log}
      modelType={modelType}
      modelId={modelId}
      failed
    />
  ),
  SMS: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MessageSquareIcon}
      iconColor="text-blue-400"
    />
  ),
  NOTE: (log) => <NotationActivityLogItem log={log} titleKey="commented" />,
  PHONE_CALL: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={PhoneIcon}
      iconColor="text-green-400"
    />
  ),
  UPDATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={RefreshCwIcon}
      iconColor="text-blue-400"
    />
  ),
  BOOKING_HOLDING_DEPOSIT_RELEASED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={HandCoinsIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_HOLDING_DEPOSIT_RELEASE_FAILED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={AlertTriangleIcon}
      iconColor="text-red-400"
    />
  ),
  BOOKING_COMPLETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={LucideCheckCircleIcon}
      iconColor="text-green-400"
    />
  ),
  INTEGRATION_JUCY_BOOKING_PUSHED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={SendIcon}
      iconColor="text-green-400"
    />
  ),
  INTEGRATION_JUCY_BOOKING_PUSHED_FAILED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={AlertCircleIcon}
      iconColor="text-red-400"
    />
  ),
  INTEGRATION_CLICK4WHEELS_BOOKING_PUSHED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={SendIcon}
      iconColor="text-green-400"
    />
  ),
  INTEGRATION_CLICK4WHEELS_BOOKING_PUSHED_FAILED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={AlertCircleIcon}
      iconColor="text-red-400"
    />
  ),
  MARKETING_EVENT_TRIGGERED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MegaphoneIcon}
      iconColor="text-fuchsia-400"
    />
  ),
  ORGANISATION_MEMBER_ADDED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserCheckIcon}
      iconColor="text-green-400"
    />
  ),
  ORGANISATION_MEMBER_REMOVED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={UserXIcon}
      iconColor="text-red-400"
    />
  ),
  ORGANISATION_MEMBER_INVITED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={SendIcon}
      iconColor="text-blue-400"
    />
  ),
  ORGANISATION_INVITATION_ACCEPTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MailCheckIcon}
      iconColor="text-green-400"
    />
  ),
  ORGANISATION_INVITATION_REVOKED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={XCircleIcon}
      iconColor="text-red-400"
    />
  ),
  WAITLIST_MATCHES_SENT: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MailCheckIcon}
      iconColor="text-indigo-400"
    />
  ),
  BOOKING_CHANGE_REQUEST_ACTIONED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MessageSquareIcon}
      iconColor="text-blue-400"
    />
  ),
  BOOKING_CUSTOMER_BLOCKED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={ShieldBanIcon}
      iconColor="text-red-400"
    />
  ),
  BOOKING_FUEL_REFUND_FORM_SENT: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MailCheckIcon}
      iconColor="text-blue-400"
    />
  ),
  BOOKING_FUEL_REFUND_FORM_SUBMITTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CheckCircle2Icon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_FUEL_REFUND_FORM_COMPLETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={LucideCheckCircleIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_FUEL_REFUND_FORM_REJECTED: (log) => (
    <DefaultActivityLogItem log={log} Icon={BanIcon} iconColor="text-red-400" />
  ),
  BOOKING_FERRY_REFUND_FORM_SENT: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={MailCheckIcon}
      iconColor="text-blue-400"
    />
  ),
  BOOKING_FERRY_REFUND_FORM_SUBMITTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CheckCircle2Icon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_FERRY_REFUND_FORM_COMPLETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={LucideCheckCircleIcon}
      iconColor="text-green-400"
    />
  ),
  BOOKING_FERRY_REFUND_FORM_REJECTED: (log) => (
    <DefaultActivityLogItem log={log} Icon={BanIcon} iconColor="text-red-400" />
  ),
  ASSIGNMENT_ON_ROAD: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={TruckIcon}
      iconColor="text-yellow-400"
    />
  ),
  ASSIGNMENT_DELIVERED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={PackageCheckIcon}
      iconColor="text-blue-400"
    />
  ),
  ASSIGNMENT_COMPLETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CircleCheckBigIcon}
      iconColor="text-green-400"
    />
  ),
  ASSIGNMENT_PAYOUT_CREATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={BanknoteIcon}
      iconColor="text-green-400"
    />
  ),
  ASSIGNMENT_PAYOUT_DELETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={BanknoteIcon}
      iconColor="text-red-400"
    />
  ),
  ASSIGNMENT_DRIVER_RATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={StarIcon}
      iconColor="text-amber-400"
    />
  ),
  INVOICE_ISSUED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileCheckIcon}
      iconColor="text-green-400"
    />
  ),
  INVOICE_VOIDED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileXIcon}
      iconColor="text-red-400"
    />
  ),
  INVOICE_PAYMENT_CREATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={BanknoteIcon}
      iconColor="text-green-400"
    />
  ),
  INVOICE_PAYMENT_UPDATED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={BanknoteIcon}
      iconColor="text-blue-400"
    />
  ),
  INVOICE_PAYMENT_DELETED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={BanknoteIcon}
      iconColor="text-red-400"
    />
  ),
  INCIDENT_REPORTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={AlertTriangleIcon}
      iconColor="text-amber-400"
    />
  ),
  INCIDENT_CLAIM_OPENED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={FileWarningIcon}
      iconColor="text-orange-400"
    />
  ),
  INCIDENT_CLAIM_PAID: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={HandCoinsIcon}
      iconColor="text-green-400"
    />
  ),
  INCIDENT_CLAIM_REJECTED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={ShieldBanIcon}
      iconColor="text-red-400"
    />
  ),
  INCIDENT_CLOSED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={CircleCheckBigIcon}
      iconColor="text-green-400"
    />
  ),
  INCIDENT_REOPENED: (log) => (
    <DefaultActivityLogItem
      log={log}
      Icon={RotateCcwIcon}
      iconColor="text-amber-400"
    />
  ),
};

export function ActivityLogItem({
  log,
  modelType,
  modelId,
}: {
  log: ActivityLogListItem;
  modelType: ModelType;
  modelId: string;
}) {
  const fn = activityLogEventNodes[log.event];

  if (!fn) {
    return (
      <DefaultActivityLogItem
        log={log}
        Icon={CircleDotIcon}
        iconColor="text-gray-400"
      />
    );
  }

  return fn(log, modelType, modelId);
}
